import { c as _c } from "react/compiler-runtime";
import { useCallback, useState } from "react";
import type { CarePlanCycleDetail } from "@/types/care-plan";
import { type CarePlanStep, type DuplicatedCycleInfo, StepType } from "./types";
interface CycleCreatedConfig {
  stepRange: [string, string];
  stepIds: string[];
  passCount: number;
  gapMinutes: number;
  parentCycleId?: string | null;
  childCycleIds?: string[];
  branchId?: string | null;
}
interface UseCycleManagementResult {
  cycles: CarePlanCycleDetail[];
  setCycles: React.Dispatch<React.SetStateAction<CarePlanCycleDetail[]>>;
  onCycleCreated: (config: CycleCreatedConfig) => void;
  renameCycle: (cycleId: string, newName: string) => void;
  duplicateCycles: (infos: DuplicatedCycleInfo[]) => void;
}
type SetStepsFn = React.Dispatch<React.SetStateAction<CarePlanStep[]>>;

/** Stamp `cycleId` onto a step and every descendant in its branches.
 * When `skipClaimed`, a step that already carries a `cycleId` keeps it —
 * preserving inner cycles when an outer cycle is drawn around them. */
function stampSubtree<T extends CarePlanStep>(step: T, cycleId: string, skipClaimed: boolean): T {
  let updated = step;
  if (step.type === StepType.DECISION && step.branches) {
    updated = {
      ...step,
      branches: step.branches.map(b => ({
        ...b,
        steps: b.steps.map(s => stampSubtree(s, cycleId, skipClaimed))
      }))
    } as T;
  }
  if (skipClaimed && (updated as CarePlanStep & {
    cycleId?: string | null;
  }).cycleId) {
    return updated;
  }
  return {
    ...updated,
    cycleId
  } as T;
}

/** Walk `steps` and stamp `cycleId` onto every step in `targetIds`,
 * propagating into nested branches so a cycled DECISION pulls its entire
 * subtree into the cycle (otherwise nested-branch contents silently fall
 * outside the cycle).
 *
 * The user's explicit targets are always force-stamped — `skipClaimed` only
 * applies inside their branch subtrees, where it preserves any deeper-nested
 * cycles. Without the force-stamp, creating an INNER cycle inside an existing
 * OUTER cycle silently no-ops because the targets already carry the outer's
 * id. */
function assignCycleIdToSteps<T extends CarePlanStep>(steps: T[], targetIds: Set<string>, cycleId: string, skipClaimed = false): T[] {
  return steps.map(step => {
    if (targetIds.has(step.id)) {
      // Force-stamp the target itself; let descendants respect skipClaimed.
      let updated = step;
      if (step.type === StepType.DECISION && step.branches) {
        updated = {
          ...step,
          branches: step.branches.map(b => ({
            ...b,
            steps: b.steps.map(s => stampSubtree(s, cycleId, skipClaimed))
          }))
        } as T;
      }
      return {
        ...updated,
        cycleId
      } as T;
    }
    if (step.type === StepType.DECISION && step.branches) {
      return {
        ...step,
        branches: step.branches.map(b => ({
          ...b,
          steps: assignCycleIdToSteps(b.steps, targetIds, cycleId, skipClaimed)
        }))
      } as T;
    }
    return step;
  });
}

/**
 * Single source of truth for cycle state.
 *
 * `cycles` holds the full cycle list (including temp-ID cycles not yet
 * saved).  Cycle creation/duplication writes `cycleId` onto the affected
 * steps via `setSteps`, so the save payload can be derived from
 * `cycles + steps` on every save (see `buildCyclesPayload`).  Failed
 * saves are idempotent — retrying re-derives the same payload from
 * current state.
 *
 * @param initialCycles - Cycles from an existing care plan or template (empty for creation)
 * @param setSteps      - Steps setter so cycle mutations can assign step.cycleId
 * @param carePlanId    - Optional care plan ID (empty string for templates or creation)
 */
export function useCycleManagement(initialCycles, setSteps, t0) {
  const $ = _c(11);
  const carePlanId = t0 === undefined ? "" : t0;
  const [cycles, setCycles] = useState(initialCycles);
  let t1;
  if ($[0] !== carePlanId || $[1] !== setSteps) {
    t1 = t2 => {
      const {
        stepIds,
        passCount,
        gapMinutes,
        parentCycleId,
        childCycleIds,
        branchId
      } = t2;
      const tempId = `temp-${Date.now()}`;
      const now = new Date().toISOString();
      const childSet = new Set(childCycleIds ?? []);
      setCycles(prev => {
        const position = prev.length;
        const updated = childSet.size > 0 ? prev.map(c => childSet.has(c.id) ? {
          ...c,
          parentCycleId: tempId
        } : c) : prev;
        return [...updated, {
          id: tempId,
          carePlanId,
          branchId: branchId ?? null,
          templateCycleId: null,
          parentCycleId: parentCycleId ?? null,
          name: null,
          passCount,
          gapMinutes,
          position,
          createdDate: now,
          updatedDate: now
        }];
      });
      const targetIds = new Set(stepIds);
      setSteps(prev_0 => assignCycleIdToSteps(prev_0, targetIds, tempId, true));
    };
    $[0] = carePlanId;
    $[1] = setSteps;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onCycleCreated = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (cycleId, newName) => {
      setCycles(prev_1 => prev_1.map(c_0 => c_0.id === cycleId ? {
        ...c_0,
        name: newName
      } : c_0));
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const renameCycle = t2;
  let t3;
  if ($[4] !== carePlanId || $[5] !== setSteps) {
    t3 = infos => {
      const now_0 = new Date().toISOString();
      setCycles(prev_2 => [...prev_2, ...infos.map((info, i) => ({
        ...info.cycle,
        carePlanId,
        position: prev_2.length + i,
        createdDate: now_0,
        updatedDate: now_0
      }))]);
      setSteps(prev_3 => {
        let next = prev_3;
        for (const info_0 of infos) {
          const scope = collectScopeSteps(next, info_0.branchId);
          if (!scope) {
            continue;
          }
          const fromIdx = scope.findIndex(s => s.id === info_0.fromStepId);
          const toIdx = scope.findIndex(s_0 => s_0.id === info_0.toStepId);
          if (fromIdx === -1 || toIdx === -1) {
            continue;
          }
          const targetIds_0 = new Set(scope.slice(fromIdx, toIdx + 1).map(_temp));
          next = assignCycleIdToSteps(next, targetIds_0, info_0.cycle.id);
        }
        return next;
      });
    };
    $[4] = carePlanId;
    $[5] = setSteps;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const duplicateCycles = t3;
  let t4;
  if ($[7] !== cycles || $[8] !== duplicateCycles || $[9] !== onCycleCreated) {
    t4 = {
      cycles,
      setCycles,
      onCycleCreated,
      renameCycle,
      duplicateCycles
    };
    $[7] = cycles;
    $[8] = duplicateCycles;
    $[9] = onCycleCreated;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}

/**
 * Flatten steps in a given branch scope (null = top-level).
 * Returns null when the branch isn't found so an empty branch (found, no steps)
 * isn't confused with a miss.
 */
function _temp(s_1) {
  return s_1.id;
}
function collectScopeSteps(steps: CarePlanStep[], branchId: string | null): CarePlanStep[] | null {
  if (branchId === null) return steps;
  for (const step of steps) {
    if (step.type === StepType.DECISION && step.branches) {
      for (const branch of step.branches) {
        if (branch.id === branchId) return branch.steps;
        const nested = collectScopeSteps(branch.steps, branchId);
        if (nested !== null) return nested;
      }
    }
  }
  return null;
}